import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../environments/environment';

export interface GeographicData {
  nacionalidades: string[];
  localidades: string[];
  provincias: string[];
}

@Injectable({
  providedIn: 'root'
})
export class GeografiaService {
  private estudiantesUrl = `${environment.apiUrl}/estudiantes`;
  
  // Static data for nacionalidades (most common first)
  private nacionalidades: string[] = [
    'ARGENTINA',
    'VENEZOLANA', 
    'PARAGUAYA',
    'COLOMBIANA',
    'PERUANA',
    'FRANCESA',
    'BRASILEÑA',
    'ESTADO UNIDENSE',
    'RUSA',
    'URUGUAYA',
    'BOLIVIANA',
    'ECUATORIANA',
    'ARGENTINA-FRANCESA',
    'ESPAÑOLA',
    'ITALIANA',
    'AZERBAIYANA',
    'CHILENA',
    'ALEMANA',
    'ARGENTINA-ESPAÑOLA',
    'ARMENIA',
    'CUBANA',
    'DOMINICANA',
    'MEXICANA',
    'UCRANIANA'
  ];

  private localidadesSubject = new BehaviorSubject<string[]>([]);
  private provinciasSubject = new BehaviorSubject<string[]>([]);
  
  localidades$ = this.localidadesSubject.asObservable();
  provincias$ = this.provinciasSubject.asObservable();

  constructor(private http: HttpClient) {
    this.loadGeographicData();
  }

  private loadGeographicData(): void {
    // Load localidades
    this.getDistinctValues('localidad').subscribe(
      localidades => this.localidadesSubject.next(localidades),
      error => console.error('Error loading localidades:', error)
    );

    // Load provincias
    this.getDistinctValues('provincia').subscribe(
      provincias => this.provinciasSubject.next(provincias),
      error => console.error('Error loading provincias:', error)
    );
  }

  private getDistinctValues(field: string): Observable<string[]> {
    return this.http.get<any[]>(`${this.estudiantesUrl}/distinct/${field}`).pipe(
      map(response => response.filter(item => item && item.trim()).sort())
    );
  }

  // Get nacionalidades (static data)
  getNacionalidades(): string[] {
    return [...this.nacionalidades];
  }

  // Get most common nacionalidades for initial display
  getMostCommonNacionalidades(): string[] {
    return this.nacionalidades.slice(0, 6);
  }

  // Filter nacionalidades based on input
  filterNacionalidades(input: string): string[] {
    if (!input || input.length === 0) {
      return this.getMostCommonNacionalidades();
    }
    return this.nacionalidades.filter(nacionalidad =>
      nacionalidad.toLowerCase().includes(input.toLowerCase())
    );
  }

  // Filter localidades based on input
  filterLocalidades(input: string): Observable<string[]> {
    return this.localidades$.pipe(
      map(localidades => {
        if (!input || input.length === 0) {
          return localidades.slice(0, 10); // Show first 10 when empty
        }
        return localidades.filter(localidad =>
          localidad.toLowerCase().includes(input.toLowerCase())
        ).slice(0, 20); // Limit to 20 results
      })
    );
  }

  // Filter provincias based on input
  filterProvincias(input: string): Observable<string[]> {
    return this.provincias$.pipe(
      map(provincias => {
        if (!input || input.length === 0) {
          return provincias.slice(0, 10); // Show first 10 when empty
        }
        return provincias.filter(provincia =>
          provincia.toLowerCase().includes(input.toLowerCase())
        ).slice(0, 20); // Limit to 20 results
      })
    );
  }
}